import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Grid, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { useSettingsStore, useApiStore } from '@core/store';
import { LoadingSpinner, NewPageTitleBar } from '@/components';
import NewWcnTable, { DataTableColumn } from '@/components/datatable/NewWcnTable';
import AllShopsSelector from '@/components/selectors/AllShopsSelector';
import AllHealthsSelector from '@/components/selectors/AllHealthsSelector';
import { HEALTH_STATES, STATUS_COLORS } from '@core/types';
import { apiClient } from '@core/api';
import { getMenuData } from '@core/data/menuData';
import { FCSecurePage, useRegisterRefresh, usePersistence } from '@/components/shared/navigation/securePage.ts';

interface HybridLoc {
    mac: string;
    firmware: string;
    shopName: string;
    lastActivity: string;
    batteryState: 'Good' | 'Medium' | 'Bad';
}

const reloadInterval = 5 * 60 * 1000;

const HybridLocBatteryMonitoring: FCSecurePage = () => {
    const { t } = useTranslation();
    const { selectedLocale } = useSettingsStore();
    const { shopIds } = useApiStore();

    const [loading, setLoading] = useState(false);
    const {
        shop: { selectedShopIds, setSelectedShopIds }
    } = usePersistence({
        shop: { preselect: 'all', inheritsPersisted: true }
    });
    const [selectedHealths, setSelectedHealths] = useState<string[]>([HEALTH_STATES.BATTERY_STATES.bad.id]);
    const [allHybridLocs, setAllHybridLocs] = useState<HybridLoc[]>([]);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const loadHybridLocs = useCallback(async () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (!selectedShopIds.length) return;

        setLoading(true);

        try {
            const shopParams = selectedShopIds.map((shopId: string) => `shopId=${shopId}`).join('&');
            const response = await apiClient.get<HybridLoc[]>(`hybridloc/batteryState?${shopParams}`);
            setAllHybridLocs(response.data);
        } catch (error) {
            console.error('Error fetching hybrid loc battery data:', error);
        } finally {
            setLastUpdate(DateTime.now());
            setLoading(false);
            timeoutRef.current = setTimeout(() => {
                loadHybridLocs();
            }, reloadInterval);
        }
    }, [selectedShopIds]);

    useRegisterRefresh(loadHybridLocs, selectedShopIds.length === 0 || selectedHealths.length === 0);

    useEffect(() => {
        setAllHybridLocs([]);
        if (selectedShopIds.length > 0) {
            loadHybridLocs();
        }
    }, [selectedShopIds, loadHybridLocs]);

    const getHealthState = useCallback((hybridLoc: HybridLoc) => {
        switch (hybridLoc.batteryState) {
            case 'Good':
                return HEALTH_STATES.BATTERY_STATES.good;
            case 'Medium':
                return HEALTH_STATES.BATTERY_STATES.medium;
            default:
                return HEALTH_STATES.BATTERY_STATES.bad;
        }
    }, []);

    const getStatusIcon = useCallback(
        (hybridLoc: HybridLoc) => {
            const healthState = getHealthState(hybridLoc);
            const colorValue = STATUS_COLORS[healthState.color as keyof typeof STATUS_COLORS];

            return (
                <Box
                    component="i"
                    className={`zmdi ${healthState.icon}`}
                    sx={{
                        fontSize: '24px',
                        color: `${colorValue} !important`,
                        mr: 1
                    }}
                />
            );
        },
        [getHealthState]
    );

    const filteredHybridLocs = React.useMemo(() => {
        if (selectedShopIds.length === 0) return [];

        if (selectedHealths.length === 0) return [];

        if (!allHybridLocs.length) return [];

        const selectedShopNames = new Set(
            selectedShopIds.map((shopId: string) => {
                const shop = shopIds.find((s) => s.id === shopId);
                return shop?.name || shopId;
            })
        );

        return allHybridLocs.filter((hybridLoc) => {
            const healthState = getHealthState(hybridLoc);
            const matchesHealthFilter = selectedHealths.includes(healthState.id);

            const matchesShopFilter = selectedShopNames.has(hybridLoc.shopName);

            return matchesHealthFilter && matchesShopFilter;
        });
    }, [allHybridLocs, selectedHealths, selectedShopIds, shopIds, getHealthState]);

    const formatDateTime = (dateString: string) => {
        return DateTime.fromISO(dateString).setLocale(selectedLocale.locale).toLocaleString(DateTime.DATETIME_SHORT);
    };

    const tableColumns: DataTableColumn<HybridLoc>[] = [
        {
            value: 'batteryState',
            text: t('battery'),
            sortable: true,
            width: 150,
            render: (hybridLoc) => getStatusIcon(hybridLoc)
        },
        {
            value: 'mac',
            text: t('lockId'),
            sortable: true,
            width: 200,
            render: (hybridLoc) => <Typography variant="body2">{hybridLoc.mac}</Typography>
        },
        {
            value: 'firmware',
            text: t('firmware'),
            sortable: true,
            width: 200,
            render: (hybridLoc) => <Typography variant="body2">{hybridLoc.firmware}</Typography>
        },
        {
            value: 'shopName',
            text: t('shop'),
            sortable: true,
            width: 200,
            render: (hybridLoc) => <Typography variant="body2">{hybridLoc.shopName}</Typography>
        },
        {
            value: 'lastActivity',
            text: t('lastActivityTime'),
            sortable: true,
            width: 200,
            render: (hybridLoc) => <Typography variant="body2">{formatDateTime(hybridLoc.lastActivity)}</Typography>
        }
    ];

    return (
        <Box>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-lock" />}
                title={`${t('hybridLocBattery')}`}
                iconSize="18px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/hybridloc/battery' }}
                t={t}
                lastUpdate={lastUpdate}
                hasAutoRefresh={true}
            />

            <Box sx={{ px: 3, pb: 3, mt: 1 }}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid
                        item
                        xs={6}
                        md={3}
                        sx={{ flexBasis: { md: '560px' }, maxWidth: { md: '560px' }, flexGrow: { md: 0 } }}
                    >
                        <AllShopsSelector value={selectedShopIds} onChange={setSelectedShopIds} label={t('shop')} />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <AllHealthsSelector
                            healthStates={HEALTH_STATES.BATTERY_STATES}
                            selectedOnStart={[HEALTH_STATES.BATTERY_STATES.bad.id]}
                            onSelectedHealthsChanged={setSelectedHealths}
                        />
                    </Grid>
                </Grid>

                {selectedShopIds.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('noData')}
                        </Typography>
                    </Box>
                )}

                {loading && selectedShopIds.length > 0 && (
                    <LoadingSpinner showBackground={false} showMessage={true} message={t('loadingData')} size={60} />
                )}

                {!loading && filteredHybridLocs.length > 0 && (
                    <div className="data-table__wrapper">
                        <Paper component="div">
                            <NewWcnTable
                                columns={tableColumns}
                                data={filteredHybridLocs}
                                sorting={{ defaultSortBy: 'batteryState' }}
                                size="small"
                                noDataMessage={t('noData')}
                            />
                        </Paper>
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default HybridLocBatteryMonitoring;
